<template>
  <v-container fluid>
    <h1 class="text-h3 mb-4">Тест системы прав доступа</h1>
    
    <!-- User info section -->
    <v-card class="mb-4">
      <v-card-title>Информация о пользователе</v-card-title>
      <v-card-text>
        <v-list density="compact">
          <v-list-item>
            <v-list-item-title>
              <strong>Аутентифицирован:</strong>
              <v-chip :color="isAuthenticated ? 'success' : 'error'" size="small" class="ml-2">
                {{ isAuthenticated ? 'Да' : 'Нет' }}
              </v-chip>
            </v-list-item-title>
          </v-list-item>
          <v-list-item>
            <v-list-item-title>
              <strong>Роли:</strong> {{ userRoles.join(', ') || 'Нет ролей' }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item>
            <v-list-item-title>
              <strong>Администратор:</strong>
              <v-chip :color="isAdmin ? 'success' : 'error'" size="small" class="ml-2">
                {{ isAdmin ? 'Да' : 'Нет' }}
              </v-chip>
            </v-list-item-title>
          </v-list-item>
          <v-list-item>
            <v-list-item-title>
              <strong>Инициализация завершена:</strong>
              <v-chip :color="isAuthInitialized ? 'success' : 'warning'" size="small" class="ml-2">
                {{ isAuthInitialized ? 'Да' : 'Нет' }}
              </v-chip>
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-card-text>
    </v-card>
    
    <!-- Composable tests -->
    <v-card class="mb-4">
      <v-card-title>Тест composable usePermissions</v-card-title>
      <v-card-text>
        <div class="text-h6 mb-2">Проверка ролей:</div>
        <v-list density="compact">
          <v-list-item>
            <v-list-item-title>hasRole('admin'): <v-chip size="small" :color="hasRole('admin') ? 'success' : 'default'">{{ hasRole('admin') }}</v-chip></v-list-item-title>
          </v-list-item>
          <v-list-item>
            <v-list-item-title>hasRole('user'): <v-chip size="small" :color="hasRole('user') ? 'success' : 'default'">{{ hasRole('user') }}</v-chip></v-list-item-title>
          </v-list-item>
          <v-list-item>
            <v-list-item-title>hasAnyRole(['admin', 'user']): <v-chip size="small" :color="hasAnyRole(['admin', 'user']) ? 'success' : 'default'">{{ hasAnyRole(['admin', 'user']) }}</v-chip></v-list-item-title>
          </v-list-item>
          <v-list-item>
            <v-list-item-title>hasAllRoles(['admin', 'user']): <v-chip size="small" :color="hasAllRoles(['admin', 'user']) ? 'success' : 'default'">{{ hasAllRoles(['admin', 'user']) }}</v-chip></v-list-item-title>
          </v-list-item>
        </v-list>
        
        <div class="text-h6 mt-4 mb-2">Проверка доступа к маршрутам:</div>
        <v-list density="compact">
          <v-list-item>
            <v-list-item-title>canAccessRoute('/dashboard'): <v-chip size="small" :color="canAccessRoute('/dashboard') ? 'success' : 'default'">{{ canAccessRoute('/dashboard') }}</v-chip></v-list-item-title>
          </v-list-item>
          <v-list-item>
            <v-list-item-title>canAccessRoute('/admin/users'): <v-chip size="small" :color="canAccessRoute('/admin/users') ? 'success' : 'default'">{{ canAccessRoute('/admin/users') }}</v-chip></v-list-item-title>
          </v-list-item>
          <v-list-item>
            <v-list-item-title>canAccessRoute('/projects'): <v-chip size="small" :color="canAccessRoute('/projects') ? 'success' : 'default'">{{ canAccessRoute('/projects') }}</v-chip></v-list-item-title>
          </v-list-item>
        </v-list>
        
        <div class="text-h6 mt-4 mb-2">Проверка действий:</div>
        <v-list density="compact">
          <v-list-item>
            <v-list-item-title>canPerformAction({ requiresAuth: true }): <v-chip size="small" :color="canPerformAction({ requiresAuth: true }) ? 'success' : 'default'">{{ canPerformAction({ requiresAuth: true }) }}</v-chip></v-list-item-title>
          </v-list-item>
          <v-list-item>
            <v-list-item-title>canPerformAction({ requiresRoles: ['admin'] }): <v-chip size="small" :color="canPerformAction({ requiresRoles: ['admin'] }) ? 'success' : 'default'">{{ canPerformAction({ requiresRoles: ['admin'] }) }}</v-chip></v-list-item-title>
          </v-list-item>
          <v-list-item>
            <v-list-item-title>canPerformAction({ requiresAnyRole: ['admin', 'user'] }): <v-chip size="small" :color="canPerformAction({ requiresAnyRole: ['admin', 'user'] }) ? 'success' : 'default'">{{ canPerformAction({ requiresAnyRole: ['admin', 'user'] }) }}</v-chip></v-list-item-title>
          </v-list-item>
        </v-list>
      </v-card-text>
    </v-card>
    
    <!-- Directive tests -->
    <v-card class="mb-4">
      <v-card-title>Тест директивы v-permission</v-card-title>
      <v-card-text>
        <div class="text-h6 mb-3">Базовые проверки:</div>
        <div class="mb-4">
          <v-btn v-permission="'admin'" color="error" class="mr-2">
            Только для админов
          </v-btn>
          <v-btn v-permission="'user'" color="primary" class="mr-2">
            Только для пользователей
          </v-btn>
          <v-btn v-permission="['admin', 'user']" color="success">
            Для админов ИЛИ пользователей
          </v-btn>
        </div>
        
        <div class="text-h6 mb-3">Сложные проверки:</div>
        <div class="mb-4">
          <v-alert v-permission="{ authenticated: true }" type="info" class="mb-2">
            Этот блок виден только аутентифицированным пользователям
          </v-alert>
          
          <v-alert v-permission="{ any: ['admin', 'moderator'] }" type="warning" class="mb-2">
            Этот блок виден админам ИЛИ модераторам
          </v-alert>
          
          <v-alert v-permission="{ all: ['admin', 'editor'] }" type="error">
            Этот блок виден только тем, кто имеет ОБЕ роли: admin И editor
          </v-alert>
        </div>
        
        <div class="text-h6 mb-3">Условные элементы интерфейса:</div>
        <v-toolbar density="compact" color="grey-lighten-4">
          <v-toolbar-title>Меню</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn variant="text">Главная</v-btn>
          <v-btn v-permission="{ authenticated: true }" variant="text">
            Профиль
          </v-btn>
          <v-btn v-permission="'admin'" variant="text" color="error">
            Администрирование
          </v-btn>
        </v-toolbar>
      </v-card-text>
    </v-card>
    
    <!-- Dynamic permission test -->
    <v-card>
      <v-card-title>Динамическая проверка прав</v-card-title>
      <v-card-text>
        <v-text-field
          v-model="testRole"
          label="Проверить роль"
          placeholder="Введите роль"
          variant="outlined"
          density="comfortable"
        ></v-text-field>
        
        <div v-if="testRole" class="my-3">
          <span>hasRole('{{ testRole }}'):</span>
          <v-chip :color="hasRole(testRole) ? 'success' : 'default'" size="small" class="ml-2">
            {{ hasRole(testRole) }}
          </v-chip>
        </div>
        
        <v-alert v-permission="testRole" type="success" class="mt-3">
          Этот блок виден только пользователям с ролью "{{ testRole }}"
        </v-alert>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { ref } from 'vue';
import { usePermissions } from '@/composables/usePermissions';

export default {
  name: 'TestPermissions',
  setup() {
    const {
      userRoles,
      isAuthenticated,
      isAuthInitialized,
      isAdmin,
      hasRole,
      hasAnyRole,
      hasAllRoles,
      canAccessRoute,
      canPerformAction
    } = usePermissions();
    
    const testRole = ref('');
    
    return {
      // Computed from composable
      userRoles,
      isAuthenticated,
      isAuthInitialized,
      isAdmin,
      
      // Methods from composable
      hasRole,
      hasAnyRole,
      hasAllRoles,
      canAccessRoute,
      canPerformAction,
      
      // Local state
      testRole
    };
  }
};
</script>

<style scoped>
/* Vuetify handles most styling */
</style>